import type { QueryClient } from '@tanstack/react-query'

import type { components } from '@/api/schema'
import { marketEndpoints } from '@/api/endpointCache'
import { getProposedPage, proposedGet } from '@/api/proposedEndpoint'
import { brokerService } from '@/features/brokers/api/brokerService'
import { LIVE_MAX_LIMIT } from '@/features/market/api/marketApi'
import {
  displayDate,
  displayTimeWithSeconds,
  kittaToCrore,
  pageTotal,
  rupeesToArba,
} from '@/features/market/api/marketMappers'
import { add, cmp, div, isZero, money, mul, sum, ZERO } from '@/lib/money'
import { tablePageSchema, type PageSize, type TablePage } from '@/schemas/table'
import {
  floorsheetBrokerOptionSchema,
  floorsheetInsightsSchema,
  floorsheetSummarySchema,
  tradeRowSchema,
  type FloorsheetBrokerOption,
  type FloorsheetInsights,
  type FloorsheetOrder,
  type FloorsheetSummary,
  type TradeRow,
} from '@/schemas/floorsheet'

type TradeWire = components['schemas']['FloorsheetTrade']
type SummaryWire = components['schemas']['FloorsheetSummary']
type BrokerWire = components['schemas']['BrokerActivity']
type MoverWire = components['schemas']['MoverRow']

/**
 * THE switch for the date picker's "Date range" tab. mm-be's floorsheet list and summary take one
 * `date`, so this is `false`: the tab is there, picking it shows "Not available yet", and no range
 * request is ever made. Flip it when `date_from`/`date_to` ship (`asks/tr1.md`).
 */
export const FLOORSHEET_RANGE_AVAILABLE: boolean = false

/**
 * THE switch for "Where the money went" on a past day. `/market/brokers/` and `/market/movers/`
 * describe today's session only, so a picked day draws both cards' heads over "Not available
 * yet", with no request, rather than today's numbers under another day's date (`asks/tr1.md`).
 */
export const FLOORSHEET_PAST_DAY_PANELS_AVAILABLE: boolean = false

/** One day, or (behind `FLOORSHEET_RANGE_AVAILABLE`) a run of days, both AD ISO. Absent: today. */
export type FloorsheetDay =
  { kind: 'today' } | { kind: 'day'; date: string } | { kind: 'range'; from: string; to: string }

/** The proposed range params: the same list and summary, cut to `date_from`..`date_to`. */
const rangeQuery = (day: Extract<FloorsheetDay, { kind: 'range' }>) => ({
  date_from: day.from,
  date_to: day.to,
})

/**
 * One page of the trade log, cut by the SERVER: the member's page, the filters the endpoint
 * accepts, the day, and one of the three orders it serves.
 *
 * `page`/`pageSize` are echoed from the request rather than read back out of `meta`: they are the
 * numbers in the URL and on the pager, and `?page=3` and the pill marked 3 must never drift. `total`
 * is the one field only the backend knows; `rows.length` is the fallback so a deployment that stops
 * merging `PageMeta` renders one honest page instead of throwing.
 */
export interface FloorsheetTradesQuery {
  page: number
  pageSize: PageSize
  symbol?: string | undefined
  buyer?: string | undefined
  seller?: string | undefined
  /** Either side — see `brokerTradesPage`. */
  broker?: string | undefined
  day: FloorsheetDay
  order: FloorsheetOrder
}

const tradePageSchema = tablePageSchema(tradeRowSchema)

const tradeRow = (row: TradeWire, names: Map<string, string>) => ({
  id: row.contract_no,
  symbol: row.symbol,
  name: names.get(row.symbol) ?? row.symbol,
  time: displayTimeWithSeconds(row.trade_time),
  buyer: row.buyer ?? '—',
  seller: row.seller ?? '—',
  kitta: row.quantity,
  rate: row.rate,
  amount: row.amount,
})

/**
 * The trade log for one broker, from the endpoint built for it.
 *
 * `?broker=` means "either side", which `/market/floorsheet/` cannot express — it takes `buyer`
 * and `seller`, and they combine with AND. `GET /api/v5/brokers/{code}/floorsheet/` IS the
 * either-side list, server-paged over today's session, newest first. It takes no date and no
 * order, so the inbound "Open floorsheet" link from a broker page lands on today's trades for that
 * broker, as it always has.
 */
const brokerTradesPage = async (
  code: string,
  query: FloorsheetTradesQuery,
  names: Map<string, string>,
): Promise<TablePage<TradeRow>> => {
  const page = await brokerService.floorsheet(code, { page: query.page, pageSize: query.pageSize })
  return tradePageSchema.parse({
    rows: page.rows.map((row) => ({
      id: row.contractNo,
      symbol: row.symbol,
      name: names.get(row.symbol) ?? row.symbol,
      time: displayTimeWithSeconds(row.tradeTime),
      buyer: row.side === 'buy' ? code : (row.counterparty ?? '—'),
      seller: row.side === 'buy' ? (row.counterparty ?? '—') : code,
      kitta: row.quantity,
      rate: row.rate,
      amount: row.amount,
    })),
    page: query.page,
    pageSize: query.pageSize,
    total: page.total,
  })
}

const summaryFrom = (summary: SummaryWire): FloorsheetSummary =>
  floorsheetSummarySchema.parse({
    date: displayDate(summary.date),
    tradesExecuted: summary.trades_executed,
    kittaTradedCr: kittaToCrore(summary.kitta_traded),
    valueTradedArba: rupeesToArba(summary.value_traded),
    companiesTraded: summary.companies_traded,
  })

export const floorsheetService = {
  summary: async (day: FloorsheetDay, client?: QueryClient): Promise<FloorsheetSummary> => {
    if (day.kind === 'range') {
      return summaryFrom(
        await proposedGet<SummaryWire>('/api/v5/market/floorsheet/summary/', rangeQuery(day)),
      )
    }
    return summaryFrom(
      await marketEndpoints(client).floorsheetSummary(day.kind === 'day' ? day.date : undefined),
    )
  },

  /**
   * The trade log, ONE SERVER PAGE AT A TIME, for the day the date button names.
   *
   * `date` goes on every request (owner ruling 10): the summary always took it, and the log used
   * not to, so a picked day re-dated the stat panel while the table stayed on the latest session.
   * `order` is the one the sort head asked for, among the three the endpoint serves.
   *
   * The either-side `broker` filter has no equivalent on this endpoint and goes to the broker's
   * own list instead (`brokerTradesPage`). The one combination no endpoint serves is that AND a
   * symbol; the screen drops the either-side chip as soon as a member narrows to a symbol.
   */
  trades: async (
    query: FloorsheetTradesQuery,
    client?: QueryClient,
  ): Promise<TablePage<TradeRow>> => {
    const api = marketEndpoints(client)
    // `live` is the company-name lookup, not a data source for the rows: a symbol outside the
    // snapshot falls back to its own ticker.
    const live = await api.live(LIVE_MAX_LIMIT)
    const names = new Map(live.rows.map((row) => [row.key, row.name]))

    if (query.broker !== undefined && query.symbol === undefined) {
      return brokerTradesPage(query.broker, query, names)
    }

    const filters = {
      page: query.page,
      page_size: query.pageSize,
      order: query.order,
      ...(query.symbol === undefined ? {} : { symbol: query.symbol }),
      ...(query.buyer === undefined ? {} : { buyer: query.buyer }),
      ...(query.seller === undefined ? {} : { seller: query.seller }),
    }
    const page =
      query.day.kind === 'range'
        ? await getProposedPage<TradeWire>('/api/v5/market/floorsheet/', {
            ...filters,
            ...rangeQuery(query.day),
          })
        : await api.floorsheet({
            ...filters,
            ...(query.day.kind === 'day' ? { date: query.day.date } : {}),
          })

    return tradePageSchema.parse({
      rows: page.rows.map((row) => tradeRow(row, names)),
      page: query.page,
      pageSize: query.pageSize,
      total: pageTotal(page.meta, page.rows.length),
    })
  },

  /**
   * The Buyer and Seller menus: the brokers `/market/brokers/` lists, by broker number.
   * Same cache entry the busiest-brokers card reads, so no extra request.
   */
  brokerOptions: async (client?: QueryClient): Promise<FloorsheetBrokerOption[]> => {
    const brokers = await marketEndpoints(client).brokers(50)
    return floorsheetBrokerOptionSchema
      .array()
      .parse(
        brokers.rows
          .map((row) => ({ code: row.code, name: row.name ?? `Broker ${row.code}` }))
          .sort((a, b) => Number(a.code) - Number(b.code) || a.code.localeCompare(b.code)),
      )
  },

  /**
   * "Where the money went".
   *
   * Busiest brokers: each broker's share of ALL trading. Every trade has a buying and a selling
   * broker, so the brokers' buy and sell turnovers summed over the whole market are twice the
   * day's value traded; a broker's (buy + sell) over twice `summary.value_traded` is its share.
   * The five largest, and the five together.
   *
   * Most traded: `/market/movers/?type=turnover`, ranked by rupees over the whole session.
   *
   * Today reads the served endpoints. A past day (or range) is only ever asked for once
   * `FLOORSHEET_PAST_DAY_PANELS_AVAILABLE` is true, through the proposed `date` params.
   */
  insights: async (day: FloorsheetDay, client?: QueryClient): Promise<FloorsheetInsights> => {
    const api = marketEndpoints(client)
    const dated =
      day.kind === 'today' ? null : day.kind === 'day' ? { date: day.date } : rangeQuery(day)
    const [brokers, busiest, summary] =
      dated === null
        ? await Promise.all([
            api.brokers(50),
            api.movers({ type: 'turnover', limit: 5 }),
            api.floorsheetSummary(),
          ])
        : await Promise.all([
            getProposedPage<BrokerWire>('/api/v5/market/brokers/', { limit: 50, ...dated }),
            getProposedPage<MoverWire>('/api/v5/market/movers/', {
              type: 'turnover',
              limit: 5,
              ...dated,
            }),
            proposedGet<SummaryWire>('/api/v5/market/floorsheet/summary/', dated),
          ])
    const allTrading = mul(money(summary.value_traded), 2)
    const shares = brokers.rows
      .map((row) => ({
        code: row.code,
        name: row.name ?? `Broker ${row.code}`,
        percent: isZero(allTrading)
          ? ZERO
          : mul(div(add(money(row.buy_turnover), money(row.sell_turnover)), allTrading), 100),
      }))
      .sort((a, b) => cmp(b.percent, a.percent))
      .slice(0, 5)

    return floorsheetInsightsSchema.parse({
      busiestBrokers: { rows: shares, totalPercent: sum(shares.map((row) => row.percent)) },
      mostTraded: busiest.rows.map((row) => ({
        symbol: row.key,
        kitta: row.volume,
        valueRs: row.turnover,
      })),
    })
  },
}
